import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import IssueDueDateCell from "./components/IssueDueDateCell";
import IssueDescriptionCell from "./components/IssueDescriptionCell";
import IssueStatusCell from "./components/IssueStatusCell";
import RCAUserWithAvatar from "../../components/RCAUserWithAvatar";
import IssueDescriptionExpandedCell from "./components/IssueDescriptionExpandedCell";
import { IIssue } from "entities/RCA/IssueEntity/IIssueEntityInterfaces";
import { IActionPlan } from "entities/RCA/ActionPlanEntity/ActionPlanEntityInterfaces";
import IssueNumberCell from "./components/IssueNumberCell";
import IssueNumberExpandedCell from "./components/IssueNumberExpandedCell";
import { ApolloQueryResult, OperationVariables } from "@apollo/client";
import IssueOptionsMenuCell from "./components/IssueOptionsMenuCell/IssueOptionsMenuCell";

interface IRCAIssuesColumns extends Omit<IMainTableColumn, "render" | "renderExpandedRow"> {
    render: (row: IIssue) => string | JSX.Element | number;
    renderExpandedRow?: (row: IActionPlan) => string | JSX.Element | number;
}

const useRCAIssuesColumns = (
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>,
): IRCAIssuesColumns[] => {
    const { t } = useTranslation();

    const columns = useMemo(
        (): IRCAIssuesColumns[] => [
            {
                id: "number",
                label: t("rca.issues.number"),
                hideSortIcon: false,
                customStyles: { width: "15%" },
                render: (row) => <IssueNumberCell issue={row} />,
                renderExpandedRow: (row) => <IssueNumberExpandedCell issue={row} />,
            },
            {
                id: "description",
                label: t("rca.issues.description"),
                hideSortIcon: false,
                customStyles: { width: "30%" },
                render: (row) => <IssueDescriptionCell issue={row} />,
                renderExpandedRow: (row) => <IssueDescriptionExpandedCell issue={row} />,
            },
            {
                id: "due_date",
                label: t("rca.issues.due_date"),
                hideSortIcon: false,
                customStyles: { width: "15%" },
                align: "center",
                render: (row) => <IssueDueDateCell issue={row} />,
            },
            {
                id: "status",
                label: t("rca.issues.status"),
                customStyles: { width: "10%" },
                hideSortIcon: false,
                render: (row) => <IssueStatusCell issue={row} />,
            },
            {
                id: "owner",
                label: t("rca.issues.owner"),
                customStyles: { width: "15%" },
                hideSortIcon: false,
                render: (row) => (
                    <RCAUserWithAvatar
                        imageUrl={row.owner?.media?.media_url || ""}
                        name={`${row.owner?.first_name} ${row.owner?.last_name}` || ""}
                    />
                ),
                renderExpandedRow: (row) => {
                    return (
                        <RCAUserWithAvatar
                            imageUrl={row.submitted_by?.media?.media_url || ""}
                            name={`${row.submitted_by?.first_name} ${row.submitted_by?.last_name}` || ""}
                        />
                    );
                },
            },
            {
                id: "options",
                label: "",
                hideSortIcon: true,
                customStyles: { width: "5%" },
                render: (row) => <IssueOptionsMenuCell refetch={refetch} issue={row} />,
                renderExpandedRow: (row) => "",
            },
        ],
        [t, refetch],
    );

    return columns;
};

export default useRCAIssuesColumns;
